import { Link } from 'react-router'
import { Euro, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { SERVICE_LABELS, SERVICE_PRICES } from '~/lib/programmatic-seo'

/**
 * Price comparison table between cities for a given service.
 * Shows regional price variations — unique content per page.
 */

// Regional price multipliers (based on real market data patterns)
const CITY_MULTIPLIERS: Record<string, { mult: number; label: string }> = {
  'Paris': { mult: 1.25, label: 'Île-de-France (+25%)' },
  'Marseille': { mult: 1.05, label: 'PACA (+5%)' },
  'Lyon': { mult: 1.15, label: 'Rhône-Alpes (+15%)' },
  'Toulouse': { mult: 1.0, label: 'Occitanie (réf.)' },
  'Nice': { mult: 1.20, label: 'Côte d\'Azur (+20%)' },
  'Nantes': { mult: 1.0, label: 'Pays de Loire (réf.)' },
  'Bordeaux': { mult: 1.10, label: 'Nouvelle-Aquitaine (+10%)' },
  'Lille': { mult: 0.95, label: 'Hauts-de-France (-5%)' },
  'Strasbourg': { mult: 1.05, label: 'Grand Est (+5%)' },
  'Montpellier': { mult: 1.0, label: 'Occitanie (réf.)' },
  'Rennes': { mult: 0.95, label: 'Bretagne (-5%)' },
  'Grenoble': { mult: 1.10, label: 'Alpes (+10%)' },
  'Toulon': { mult: 1.05, label: 'Var (+5%)' },
  'Reims': { mult: 0.95, label: 'Grand Est (-5%)' },
  'Annecy': { mult: 1.15, label: 'Haute-Savoie (+15%)' },
}

interface CityPriceComparisonProps {
  serviceSlug: string
  currentCity: string
  cities?: string[]
}

export function CityPriceComparison({ serviceSlug, currentCity, cities }: CityPriceComparisonProps) {
  const price = SERVICE_PRICES[serviceSlug]
  const svc = SERVICE_LABELS[serviceSlug]
  if (!price || !svc) return null

  // Use provided cities or default top cities
  const compareCities = (cities ?? Object.keys(CITY_MULTIPLIERS)).filter((c) => c !== currentCity).slice(0, 7)
  const currentMult = CITY_MULTIPLIERS[currentCity]?.mult ?? 1.0

  const currentMin = Math.round(price.min * currentMult)
  const currentMax = Math.round(price.max * currentMult)

  return (
    <div className="rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
      <div className="border-b border-border px-6 py-4">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Euro className="h-5 w-5 text-primary" />
          Comparaison des prix — {svc.name} par ville
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Les tarifs varient de -5% à +25% selon la région. Voici les prix comparés à {currentCity}.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-5 py-2.5">Ville</th>
              <th className="px-5 py-2.5">Région</th>
              <th className="px-5 py-2.5 text-right">Prix min</th>
              <th className="px-5 py-2.5 text-right">Prix max</th>
              <th className="px-5 py-2.5 text-center">vs {currentCity}</th>
              <th className="px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {/* Current city — highlighted */}
            <tr className="bg-primary-50">
              <td className="px-5 py-3 text-sm font-bold text-primary">{currentCity}</td>
              <td className="px-5 py-3 text-xs text-slate-500">{CITY_MULTIPLIERS[currentCity]?.label ?? 'France'}</td>
              <td className="px-5 py-3 text-right text-sm font-bold text-primary">{currentMin} €</td>
              <td className="px-5 py-3 text-right text-sm font-bold text-primary">{currentMax} €</td>
              <td className="px-5 py-3 text-center">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">Vous</span>
              </td>
              <td className="px-5 py-3"></td>
            </tr>
            {/* Other cities */}
            {compareCities.map((city) => {
              const mult = CITY_MULTIPLIERS[city]?.mult ?? 1.0
              const cityMin = Math.round(price.min * mult)
              const cityMax = Math.round(price.max * mult)
              const diff = Math.round((mult - currentMult) / currentMult * 100)
              const artisanSlug = svc.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')

              return (
                <tr key={city} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 text-sm font-medium text-slate-900">{city}</td>
                  <td className="px-5 py-3 text-xs text-slate-400">{CITY_MULTIPLIERS[city]?.label ?? ''}</td>
                  <td className="px-5 py-3 text-right text-sm text-slate-900">{cityMin} €</td>
                  <td className="px-5 py-3 text-right text-sm text-slate-900">{cityMax} €</td>
                  <td className="px-5 py-3 text-center">
                    {diff > 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-600">
                        <TrendingUp className="h-3 w-3" /> +{diff}%
                      </span>
                    ) : diff < 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
                        <TrendingDown className="h-3 w-3" /> {diff}%
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-xs text-slate-400">
                        <Minus className="h-3 w-3" /> =
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Link to={`/prix/${artisanSlug}-${citySlug}`} className="text-xs text-primary hover:underline">
                      Voir →
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="border-t border-border bg-slate-50 px-5 py-2.5 text-[10px] text-slate-400">
        * Estimations basées sur les écarts régionaux constatés en 2026. Prix indicatifs TTC.
      </div>
    </div>
  )
}
